import { homeAddressEntry, US_STATE, US_ZIP } from '@/services/patient-address';
import { fetchBot } from '@/services/services';
import { CoverageAreaState } from '@bariendo/lib/patient-access-script';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import type { CoverageAreaResult, CoverageLookupEvent } from '../../../../../../bots/src/handlers/coverage-lookup';

/**
 * Whether insurance is offered where the patient lives (BAR-2919), for the
 * insurance section's state rule.
 *
 * The states come from bariendo-be (`COVERAGE_STATES`, through the
 * `coverage-lookup` bot), never a list in Orbit. The patient's state is the
 * state of their ZIP, resolved by bariendo-be the way signup resolves it
 * (signup writes only the ZIP to the Patient), else a two-letter state on
 * their address. Sends the ZIP only.
 *
 * Not read while `enabled` is false: a saved note does not look it up, and its
 * rule then skips the state clause.
 */
export function useCoverageArea(patient: Patient | undefined, enabled: boolean): CoverageAreaState | undefined {
  const medplum = useMedplum();
  const [area, setArea] = useState<CoverageAreaState | undefined>(enabled ? { status: 'loading' } : undefined);

  // Where the patient lives: the home address, the one the card confirms (BAR-3001).
  const address = homeAddressEntry(patient);
  const zip = address?.postalCode?.trim();
  // Free text on the Patient ("South Carolina" is possible), so only a two-letter
  // code counts, and only behind the state bariendo-be resolves from the ZIP.
  const typedState = address?.state?.trim().toUpperCase();
  const addressState = typedState && US_STATE.test(typedState) ? typedState : undefined;
  // `patient` is read only for being there; its identity changes on every live update.
  const hasPatient = Boolean(patient);

  useEffect(() => {
    // Kept when already loading: a fresh object on every run would re-render forever.
    const loading = (current: CoverageAreaState | undefined): CoverageAreaState =>
      current?.status === 'loading' ? current : { status: 'loading' };
    if (!enabled || !hasPatient) {
      setArea((current) => (enabled ? loading(current) : undefined));
      return;
    }
    let cancelled = false;
    setArea(loading);
    // The five-digit ZIP alone: enough for the state, and less to share.
    const input: CoverageLookupEvent = {
      action: 'coverageArea',
      zip: zip && US_ZIP.test(zip) ? zip.slice(0, 5) : undefined,
    };
    fetchBot<CoverageAreaResult>(medplum, 'coverage-lookup', input)
      .then((result) => {
        if (cancelled) return;
        // Null: bariendo-be doesn't serve the lookup yet. Read as a failure.
        if (!result) {
          setArea({ status: 'error', state: addressState ?? null });
          return;
        }
        const { coverageStates, state } = result;
        const patientState = state ?? addressState ?? null;
        setArea({
          status: 'ready',
          state: patientState,
          offered: Boolean(patientState) && coverageStates.includes(patientState as string),
        });
      })
      .catch(() => {
        // The state the address gives still prefills travel and scopes the
        // insurer list, but never gates the section.
        if (!cancelled) setArea({ status: 'error', state: addressState ?? null });
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, enabled, hasPatient, zip, addressState]);

  return area;
}

/**
 * The state whose insurers the card offers: only one bariendo-be resolved. On a
 * failed lookup the section stays open, so the list does too (every state's),
 * rather than narrowing to an address state nothing has checked.
 */
export function insurerListState(area: CoverageAreaState | undefined): string | undefined {
  return area?.status === 'ready' ? (area.state ?? undefined) : undefined;
}
